import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Gift, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND_COLORS } from "@/lib/menuData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WelcomePopup() {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Check if popup has been shown before
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcomePopup");
    if (hasSeenWelcome) return;

    // Check if welcome popup is enabled in admin settings
    const checkEnabled = async () => {
      try {
        const res = await axios.get(`${API}/welcome-popup-enabled`);
        if (res.data.enabled) {
          // Small delay for better UX
          setTimeout(() => setShow(true), 1500);
        }
      } catch (e) {
        // If API fails, don't show popup
        console.error("Failed to check welcome popup status");
      }
    };

    checkEnabled();
  }, []);

  const handleClose = () => {
    localStorage.setItem("hasSeenWelcomePopup", "true");
    setShow(false);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText("WELCOME10");
    setCopied(true);
    toast.success("Code copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
      data-testid="welcome-popup-overlay"
    >
      <div 
        className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl animate-fade-in"
        style={{ backgroundColor: "white" }}
      >
        {/* Close Button */}
        <button
          data-testid="close-welcome-popup"
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 hover:bg-white transition-colors"
        >
          <X className="h-5 w-5 text-slate-600" />
        </button>

        {/* Header */}
        <div 
          className="px-6 pt-10 pb-6 text-center"
          style={{ backgroundColor: BRAND_COLORS.deepTeal }}
        >
          <div 
            className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
          >
            <Gift className="h-8 w-8 text-white" />
          </div>
          <h2 
            className="text-xl font-extrabold text-white mb-1"
            style={{ fontFamily: "Montserrat" }}
          >
            Welcome to the Island!
          </h2>
          <p className="text-white/80 text-sm">
            Get 10% off your first order
          </p>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <p className="text-sm text-slate-600 mb-4">
            Use this promo code at checkout to save 10% on your entire order:
          </p>

          {/* Promo Code Box */}
          <div 
            className="bg-slate-50 rounded-2xl p-4 mb-4 border-2 border-dashed"
            style={{ borderColor: BRAND_COLORS.tropicalAqua }}
          >
            <p 
              className="text-2xl font-extrabold tracking-wider"
              style={{ fontFamily: "Montserrat", color: BRAND_COLORS.deepTeal }}
            >
              WELCOME10
            </p>
          </div>

          {/* Copy Button */}
          <Button
            data-testid="copy-welcome-code"
            onClick={handleCopyCode}
            className="w-full h-12 rounded-xl font-bold text-white mb-3"
            style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
          >
            {copied ? (
              <>
                <Check className="h-5 w-5 mr-2" /> Copied!
              </>
            ) : (
              <>
                <Copy className="h-5 w-5 mr-2" /> Copy Code
              </>
            )}
          </Button>

          <Button
            data-testid="continue-without-code"
            variant="ghost"
            className="w-full text-sm"
            onClick={handleClose}
          >
            Continue without code
          </Button>

          <p className="text-[10px] text-slate-400 mt-4">
            Valid for first-time customers only. Cannot be combined with other offers.
          </p>
        </div>
      </div>
    </div>
  );
}
